import { useCallback, useMemo, useState } from 'react';
import * as apiCloudflare from 'api/apiCloudflare';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';

interface UseProposalImagesParams {
    proposalLogo: string;
    proposalBackground: string;
    loading?: boolean;
    onChange: (patch: { proposalLogo?: string; proposalBackground?: string }) => void;
}

const uploadSingleImage = async (
    files: File[],
    fallbackValue: string,
    keyPrefix: string,
    setProgress: (value: number | boolean) => void,
    clearFiles: () => void,
): Promise<string> => {
    if (!files.length) return fallbackValue;
    const [file] = files;
    const uploadConfigs = await apiCloudflare.getCloudflareUploadUrls(
        [
            {
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
                fileSize: file.size,
            },
        ],
        {},
        { keyPrefix },
    );
    if (!uploadConfigs?.length) return fallbackValue;

    setProgress(1);
    const uploaded = await uploadFilesWithOverallProgress([file], uploadConfigs, setProgress);
    setProgress(false);
    const uploadedFile = uploaded?.[0];
    if (!uploadedFile?.success) return fallbackValue;
    clearFiles();
    return String(uploadedFile.config?.publicUrl || fallbackValue);
};

const useProposalImages = ({
    proposalLogo,
    proposalBackground,
    loading = false,
    onChange,
}: UseProposalImagesParams) => {
    const [logoFiles, setLogoFiles] = useState<File[]>([]);
    const [logoProgress, setLogoProgress] = useState<number | boolean>(false);
    const [deletingLogo, setDeletingLogo] = useState(false);
    const [backgroundFiles, setBackgroundFiles] = useState<File[]>([]);
    const [backgroundProgress, setBackgroundProgress] = useState<number | boolean>(false);
    const [deletingBackground, setDeletingBackground] = useState(false);

    const isImageBusy = useMemo(
        () =>
            loading ||
            deletingLogo ||
            deletingBackground ||
            logoProgress !== false ||
            backgroundProgress !== false,
        [loading, deletingLogo, deletingBackground, logoProgress, backgroundProgress],
    );

    const removeImage = useCallback(
        async (
            value: string,
            setDeleting: (state: boolean) => void,
            clearFiles: () => void,
            onCleared: () => void,
        ) => {
            if (!value) return;
            setDeleting(true);
            await apiCloudflare.deleteCloudflareFiles({
                publicUrls: [value],
            });
            setDeleting(false);
            clearFiles();
            onCleared();
        },
        [],
    );

    const removeLogo = useCallback(
        () =>
            removeImage(proposalLogo, setDeletingLogo, () => setLogoFiles([]), () => onChange({ proposalLogo: '' })),
        [proposalLogo, onChange, removeImage],
    );

    const removeBackground = useCallback(
        () =>
            removeImage(
                proposalBackground,
                setDeletingBackground,
                () => setBackgroundFiles([]),
                () => onChange({ proposalBackground: '' }),
            ),
        [proposalBackground, onChange, removeImage],
    );

    const uploadPendingImages = useCallback(async () => {
        const [uploadedLogo, uploadedBackground] = await Promise.all([
            uploadSingleImage(logoFiles, proposalLogo, 'proposal-logo', setLogoProgress, () => setLogoFiles([])),
            uploadSingleImage(
                backgroundFiles,
                proposalBackground,
                'proposal-background',
                setBackgroundProgress,
                () => setBackgroundFiles([]),
            ),
        ]);

        return {
            proposalLogo: uploadedLogo,
            proposalBackground: uploadedBackground,
        };
    }, [logoFiles, proposalLogo, backgroundFiles, proposalBackground]);

    return {
        logoFiles,
        setLogoFiles,
        logoProgress,
        deletingLogo,
        removeLogo,
        backgroundFiles,
        setBackgroundFiles,
        backgroundProgress,
        deletingBackground,
        removeBackground,
        isImageBusy,
        uploadPendingImages,
    };
};

export default useProposalImages;
